import React, { FC, MouseEventHandler, useMemo } from 'react';

import {
  Container,
  ContainerProps,
  Tabs,
  TabsProvider,
} from '@lglp/components';
import {
  Image,
  MainMenuItem,
  MenuCta,
  MenuItem,
  SearchMenu,
} from '@lglp/prismic-client';
import { isVoid } from '@lglp/utils';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../reducers';
import { getMainMenuLinksIds } from '../getMainMenuLinksIds';
import { HeaderLogo } from '../HeaderLogo';
import { MenuCtas } from '../MenuCtas';
import { MenuLocales } from '../MenuLocales';
import { MenuRechercher } from '../MenuRechercher';
import RegionMenu from '../RegionMenu';
import { CloseMenu } from './CloseMenu';
import DesktopContext, { useInitializeContext } from './DesktopContext';
import styles from './DesktopLayout.module.scss';
import { MenuContent } from './MenuContent';

export const DesktopLayout: FC<DesktopLayoutProps> = ({
  menuItems,
  searchMenu,
  menuCtas,
  logo,
  className,
  ...rest
}) => {
  const providerValue = useInitializeContext();
  const mainDocument = useSelector((state: RootState) => state.main?.document);
  // Gets an object of main menu labels associated with links uid of submenu items
  const mainMenuLinks = useMemo(
    () => getMainMenuLinksIds(menuItems),
    [menuItems]
  );

  const onOpenMenu =
    (menuItem: MenuItem): MouseEventHandler =>
    (event) => {
      if (providerValue.isMenuOpen(0, menuItem)) {
        providerValue.close();
      } else {
        providerValue.openMenu(0, menuItem);
        event.preventDefault();
      }
    };

  return (
    <DesktopContext.Provider value={providerValue}>
      <Container className={[styles.desktopMenu, className]} {...rest}>
        <HeaderLogo image={logo} />

        <Container>
          <Container as="nav">
            <TabsProvider uid="desktop-header-menu-tabs">
              <Tabs.List as="ul" className={styles.menu}>
                {menuItems?.map((menuItem, i) => (
                  <Container key={i} as="li" onClick={onOpenMenu(menuItem)}>
                    <Tabs.Button
                      index={i}
                      aria-selected={
                        providerValue.isMenuOpen(0, menuItem) ||
                        mainMenuLinks[menuItem.label].some(
                          (id: String) => mainDocument?.id === id
                        )
                      }
                    >
                      <span>{menuItem.label}</span>
                    </Tabs.Button>
                  </Container>
                ))}
                <MenuCtas menuCtas={menuCtas} className={styles.ctaMenu} />
              </Tabs.List>

              <Container className={styles.openedMenu}>
                {!isVoid(providerValue.openedMenus) && (
                  <>
                    <CloseMenu />
                    {menuItems.map((menuItem, i) => (
                      <Tabs.Panel
                        index={i}
                        key={i}
                        className={styles.menuContent}
                      >
                        <MenuContent menuItem={menuItem} />
                      </Tabs.Panel>
                    ))}
                  </>
                )}
              </Container>
            </TabsProvider>
          </Container>
        </Container>
      </Container>

      <Container className={styles.extraMenu}>
        <RegionMenu />
        {searchMenu && (
          <MenuRechercher search={searchMenu} className={styles.searchMenu} />
        )}
        <MenuLocales className={styles.localsMenu} />
      </Container>
    </DesktopContext.Provider>
  );
};

type DesktopLayoutProps = ContainerProps & {
  menuItems: MainMenuItem[];
  searchMenu: SearchMenu;
  menuCtas: MenuCta[];
  logo: Image;
};
